/** Callbacks the loop drives every frame. */
export interface LoopCallbacks {
  /** Advance the simulation by one fixed step (`fixedDt` seconds). */
  update(fixedDt: number): void;
  /** Draw a frame. `alpha` is 0..1 progress toward the next fixed step. */
  render(alpha: number): void;
}

/** Timing configuration for {@link GameLoop}. */
export interface LoopConfig {
  updateHz: number;
  maxStepsPerFrame: number;
  maxFrameDelta: number;
}

/**
 * Fixed-timestep game loop with a variable render rate.
 *
 * The simulation always advances in equal `1 / updateHz` steps — this keeps
 * gameplay deterministic regardless of display refresh rate. Rendering happens
 * once per animation frame and receives an interpolation `alpha` so motion
 * stays smooth even when the render rate and update rate differ.
 *
 * See "Fix Your Timestep!" (Glenn Fiedler) for the underlying pattern.
 */
export class GameLoop {
  private rafId = 0;
  private running = false;
  private lastTime = 0;
  private accumulator = 0;
  private readonly stepSec: number;

  constructor(
    private readonly callbacks: LoopCallbacks,
    private readonly config: LoopConfig,
  ) {
    this.stepSec = 1 / config.updateHz;
  }

  /** Whether the animation loop is currently scheduled. */
  get isRunning(): boolean {
    return this.running;
  }

  /** Start (or resume) the loop. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Stop the loop. Safe to call when already stopped. */
  stop(): void {
    this.running = false;
    if (this.rafId !== 0) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private readonly tick = (now: number): void => {
    if (!this.running) return;

    let delta = (now - this.lastTime) / 1000;
    this.lastTime = now;
    // Clamp huge deltas (e.g. after a backgrounded tab) so we never fast-forward.
    if (delta > this.config.maxFrameDelta) {
      delta = this.config.maxFrameDelta;
    }

    this.accumulator += delta;
    let steps = 0;
    while (this.accumulator >= this.stepSec && steps < this.config.maxStepsPerFrame) {
      this.callbacks.update(this.stepSec);
      this.accumulator -= this.stepSec;
      steps += 1;
    }
    // If we still owe time after hitting the cap, drop it (anti spiral-of-death).
    if (steps === this.config.maxStepsPerFrame && this.accumulator > this.stepSec) {
      this.accumulator = 0;
    }

    this.callbacks.render(this.accumulator / this.stepSec);
    this.rafId = requestAnimationFrame(this.tick);
  };
}
